import { ADMIN_LOGIN_ROUTE as loginPath, adminRoutePath } from '@/utils/admin-page-path';
import type {RequestOptions} from '@@/plugin-request/request';
import type {RequestConfig} from '@umijs/max';
import {history} from '@umijs/max';
import {message, notification} from 'antd';
import {Auth} from "@/utils";
import qs from 'qs';

// 错误处理方案： 错误类型
enum ErrorShowType {
  SILENT = 0,
  WARN_MESSAGE = 1,
  ERROR_MESSAGE = 2,
  NOTIFICATION = 3,
  REDIRECT = 9,
}

// 与后端约定的响应数据格式
interface ResponseStructure {
  success: boolean;
  data: any;
  errorCode?: number;
  errorMessage?: string;
  showType?: ErrorShowType;
}


const buildLoginRedirectSearch = () => {
  if (typeof window === 'undefined') {
    return '';
  }
  const {pathname, search, hash} = window.location;
  return new URLSearchParams({
    redirect: `${pathname}${search}${hash || ''}`,
  }).toString();
};

export const isAuthExpiredError = (errorMessage?: string, statusCode?: number) => {
  const normalizedMessage = (errorMessage || '').trim().toLowerCase();
  return statusCode === 401 || normalizedMessage === 'auth header is empty' || normalizedMessage === 'token is expired';
};

// 只允许请求所属的登录态退出，防止过期请求的迟到响应清除新登录。
const expireRequestSession = (requestToken: string | undefined) => {
  const currentToken = Auth.getToken();
  // 本地过期检查可能已清空旧 token；没有新会话时仍需回到登录页。
  if (requestToken === undefined || (currentToken && requestToken !== currentToken)) return;
  redirectToLoginSilently();
};

// 主动退出及布局守卫无需请求上下文；异步请求失败必须先经过上面的会话归属检查。
export const redirectToLoginSilently = () => {
  Auth.removeToken();
  if (adminRoutePath(history.location.pathname) === loginPath) {
    return;
  }
  history.replace({
    pathname: loginPath,
    search: buildLoginRedirectSearch(),
  });
};

/**
 * @name 错误处理
 * pro 自带的错误处理， 可以在这里做自己的改动
 * @doc https://umijs.org/docs/max/request#配置
 */
export const errorConfig: RequestConfig = {
  // paramsSerializer: params => qs.stringify(params, {indices: true}),
  paramsSerializer: params => qs.stringify(params, {arrayFormat: 'repeat'}),
  // 错误处理： umi@3 的错误处理方案。
  errorConfig: {
    // 错误抛出
    errorThrower: (res) => {
      const {success, data, errorCode, errorMessage, showType} =
        res as unknown as ResponseStructure;
      if (!success) {
        const error: any = new Error(errorMessage);
        error.name = 'BizError';
        error.info = {errorCode, errorMessage, showType, data};
        message.error(errorMessage);
        // throw error; // 抛出自制的错误
      }
    },
    // 错误接收及处理
    errorHandler: (error: any, opts: any) => {
      if (opts?.skipErrorHandler) throw error;
      // 我们的 errorThrower 抛出的错误。
      if (error.name === 'BizError') {
        const errorInfo: ResponseStructure | undefined = error.info;
        if (errorInfo) {
          const {errorMessage, errorCode} = errorInfo;
          switch (errorInfo.showType) {
            case ErrorShowType.SILENT:
              // do nothing
              break;
            case ErrorShowType.WARN_MESSAGE:
              message.warning(errorMessage);
              break;
            case ErrorShowType.ERROR_MESSAGE:
              message.error(errorMessage);
              break;
            case ErrorShowType.NOTIFICATION:
              notification.open({
                description: errorMessage,
                message: errorCode,
              });
              break;
            case ErrorShowType.REDIRECT:
              // TODO: redirect
              break;
            default:
              message.error(errorMessage);
          }
        }
      } else if (error.response) {
        // Axios 的错误
        // 请求成功发出且服务器也响应了状态码，但状态代码超出了 2xx 的范围
        const response = error.response;
        const requestConfig = response.config || error.config;
        if (isAuthExpiredError(response.data?.errorMessage, response.status) || response.data?.errorCode === 401) {
          // 登录接口的 401 表示密码或验证码校验失败，不代表已有会话过期。
          if (requestConfig?.url?.split('?')[0] === '/api/admin/login') {
            message.error(response.data?.errorMessage || '登录失败，请检查账号、密码和验证码');
          } else {
            expireRequestSession(requestConfig?.__adminAuthToken);
          }
          return;
        }
        message.error(`Response status:${error.response.status}`);
      } else if (error.request) {
        // 请求已经成功发起，但没有收到响应
        // \`error.request\` 在浏览器中是 XMLHttpRequest 的实例，
        // 而在node.js中是 http.ClientRequest 的实例
        message.error('None response! Please retry.');
      } else {
        // 发送请求时出了点问题
        message.error('Request error, please retry.');
      }

    },
  },

  // 请求拦截器
  requestInterceptors: [
    (config: RequestOptions) => {
      // console.log(config)
      // 拦截请求配置，进行个性化处理。
      const url = config?.url;

      const token = Auth.getToken();
      const token_type = Auth.getTokenType();
      // 空字符串也必须记录：无认证的旧请求同样不能使新会话退出。
      config.__adminAuthToken = token;

      if (token) {
        config.headers = {
          ...config.headers,
          Authorization: `${token_type} ${token}`,
        };
      }

      return {...config, url};
    },
  ],

  // 响应拦截器
  responseInterceptors: [
    (response) => {
      const data = response.data as ResponseStructure;
      // 后端使用 HTTP 200 包装认证失败。此处保留请求上下文再抛出，
      // errorThrower 只有响应体，无法安全判断失败属于哪次登录。
      if (data?.success === false && isAuthExpiredError(data.errorMessage, data.errorCode)) {
        throw Object.assign(new Error(data.errorMessage || '登录已失效'), {response});
      }
      return response;
    },
  ],
};
